import React, { useCallback, useState } from 'react';

import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';

import type { LinkType as ApiLinkType } from '../../link/types';
import { useFlagsWithOverrides } from '../../../hooks/useFlagsWithOverrides';
import { AddLinkView } from './AddLinkView';

type LinkType = {
  type: ApiLinkType;
  text: string;
};

export const LINK_TYPES: LinkType[] = [
  {
    type: 'IncreaseLink',
    text: 'Increase',
  } as const,
  {
    type: 'UnitLink',
    text: 'Unit',
  } as const,
  {
    type: 'CustomLink',
    text: 'Custom',
  } as const,
  {
    type: 'StripeLink',
    text: 'Stripe',
  } as const,
];

type OnboardLinkFormWithTypesProps = {
  linkTypes: LinkType[];
  onSelectLinkType: (linkType: ApiLinkType) => void;
};

export const OnboardLinkFormWithTypes: React.FC<
  OnboardLinkFormWithTypesProps
> = ({ linkTypes, onSelectLinkType }) => (
  <div className="space-y-f2 w-full">
    <div className="flex flex-col">
      {linkTypes.map(({ type, text }) => (
        <button
          key={type}
          type="button"
          className="flex flex-row h-f2 items-center border-b-[1px] bg-main-200 border-canvas justify-between hover:bg-negative group-focus:bg-negative"
          onClick={() => onSelectLinkType(type)}
        >
          <div className="flex items-center space-x-[2px]">
            <LinkIcon type={type} />
            <span>{text}</span>
          </div>
          <span>+</span>
        </button>
      ))}
    </div>
  </div>
);

export const OnboardLinkForm = ({
  onSelectLinkType,
}: {
  onSelectLinkType: (linkType: ApiLinkType) => void;
}) => {
  const flags = useFlagsWithOverrides([
    'unit-integration-enabled',
    'stripe-integration-enabled',
  ]);
  let linkTypes = LINK_TYPES;
  if (!flags['unit-integration-enabled'].enabled) {
    linkTypes = linkTypes.filter(({ type }) => type !== 'UnitLink');
  }
  if (!flags['stripe-integration-enabled'].enabled) {
    linkTypes = linkTypes.filter(({ type }) => type !== 'StripeLink');
  }
  return (
    <OnboardLinkFormWithTypes
      onSelectLinkType={onSelectLinkType}
      linkTypes={linkTypes}
    />
  );
};

type OnboardLinkModalProps = {
  onClose: VoidFunction;
  refresh: VoidFunction;
};
export const OnboardLinkModal = ({
  onClose,
  refresh,
}: OnboardLinkModalProps) => {
  const [selectedLinkType, setSelectedLinkType] = useState<ApiLinkType>();

  const onCreate = useCallback(() => {
    refresh();
    onClose();
  }, [refresh, onClose]);
  return (
    <Modal
      onClose={onClose}
      header={<span>{selectedLinkType ? 'Link Details' : 'Choose Link'}</span>}
      width="sm"
      body={
        <div className=" bg-canvas">
          {selectedLinkType ? (
            <AddLinkView linkType={selectedLinkType} onCreate={onCreate} />
          ) : (
            <OnboardLinkForm onSelectLinkType={setSelectedLinkType} />
          )}
        </div>
      }
    />
  );
};
